import { User } from '@repo/domain/user';
import env from '@repo/env';

/**
 * Generates a string with multiple domain patterns into a list of regex
 * @param {string} domains  The list of domains divided by commas (,)
 * @returns List of domains regex
 */
const getTraceUrls = (domains = ''): RegExp[] | undefined => {
  if (!domains) {
    return undefined;
  }
  const domainList = domains?.split(',') || [];
  return domainList.map((url) => new RegExp(url));
};

/**
 * Initialize the Coralogix RUM monitoring
 */
export const initMonitoring = async (): Promise<void> => {
  if (!env.coralogix.isEnabled) {
    return;
  }
  const { CoralogixRum } = await import('@coralogix/browser');

  if (CoralogixRum.isInited) {
    // Check if Coralogix is already running
    return;
  }

  const domains = getTraceUrls(env.coralogix.traceDomains);
  const ignoreUrls = getTraceUrls(env.coralogix.traceIgnoredDomains);

  CoralogixRum.init({
    application: env.coralogix.applicationName,
    coralogixDomain: 'EU2',
    debug: env.coralogix.debug,
    environment: env.coralogix.environment || 'development',
    ignoreErrors: [
      `Failed to read the 'cookie' property from 'Document'`, // currently unknown error on android browsers
      /OfflineError/, // our custom offline error exception
      /Intercom/, // intercom console error
      // Temporarily filter known clarityjs (3rd party) exceptions below, until it's fixed: https://github.com/microsoft/clarity/issues/656
      `Cannot read properties of null (reading 'sessionId')`,
      `null is not an object (evaluating 'Qr.sessionId')`,
      `Cannot read properties of null (reading 'version')`,
      `null is not an object (evaluating 'yi.version')`,
    ],
    ignoreUrls,
    public_key: env.coralogix.authenticationToken,
    sessionConfig: {
      alwaysTrackSessionsWithErrors: env.coralogix.trackSessionsWithErrors,
      sessionSampleRate: env.coralogix.sessionSampleRate,
    },
    sessionRecordingConfig: {
      autoStartSessionRecording: true,
      enable: env.coralogix.sessionRecordingEnabled,
      maskAllInputs: env.coralogix.maskAllInputs,
      maskInputOptions: { password: true },
      recordConsoleEvents: env.coralogix.sessionRecordingConsoleEnabled,
      recordCanvas: env.coralogix.sessionRecordingCanvasEnabled,
      sampling: {
        canvas: env.coralogix.sessionRecordingCanvasFPS,
      },
      sessionRecordingSampleRate: env.coralogix.sessionRecordingSampleRate,
      dataURLOptions: {
        type: 'image/webp',
        quality: 0.5,
      },
    },
    networkExtraConfig: [
      {
        url: /.*/,
        reqHeaders: ['If-None-Match'],
        resHeaders: ['Cache-Control', 'ETag'],
      },
    ],
    traceParentInHeader: {
      enabled: env.coralogix.traceHeaderEnabled,
      options: {
        allowedTracingUrls: domains,
        propagateTraceHeaderCorsUrls: domains,
      },
    },
    version: env.coralogix.appVersion,
  });
};

/**
 * Set user information on the Coralogix RUM context
 * @param {User} user  User data from the current logged in user
 */
export const setUserContextMonitoring = async (user: User): Promise<void> => {
  if (!env.coralogix.isEnabled) {
    return;
  }

  const { CoralogixRum } = await import('@coralogix/browser');

  if (!CoralogixRum.isInited) {
    return;
  }

  CoralogixRum.setUserContext({
    user_id: user.id,
    user_name: user.slug,
    user_email: user.email,
    user_metadata: {
      plan: user.plan,
    },
  });
};
